/**
 * pdfExport.js
 * Generates a branded PDF from AI Explorer analysis data using jsPDF.
 * Builds entirely from parsed data — no DOM scraping.
 *
 * Usage:
 *   import { generateAnalysisPDF } from './pdfExport';
 *   generateAnalysisPDF({ query, conceptTags, response, sections, citedEntries, timestamp });
 */

import { jsPDF } from 'jspdf';
import { buildAnalysisSummary } from './analysisSummary';

// Hardcoded logo PNG as base64 — eliminates async loading and canvas conversion issues.
// Generated from public/logo-ckg-light-512.png
const LOGO_BASE64 = 'data:image/png;base64,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';


// =========================================
// Layout Constants
// =========================================

const PAGE_W = 612;   // Letter width in points
const PAGE_H = 792;   // Letter height in points
const MARGIN_L = 56;
const MARGIN_R = 56;
const MARGIN_T = 48;
const CONTENT_W = PAGE_W - MARGIN_L - MARGIN_R; // 500
const LIST_INDENT = 14;  // gutter for a list marker; body wraps inside it (D7)

// Reserved footer zone — body content never enters this area
const FOOTER_ZONE = 36;                        // 36pt reserved at bottom of each page
const CONTENT_BOTTOM = PAGE_H - FOOTER_ZONE;   // 756 — body text stops here
const FOOTER_RULE_Y = CONTENT_BOTTOM + 8;      // 764 — horizontal rule
const FOOTER_TEXT_Y = FOOTER_RULE_Y + 14;      // 778 — "Generated by..." and "Page X of Y"

// =========================================
// Color Palette
// =========================================

const C = {
  teal:      [92, 137, 157],   // #5C899D — logo mark, tagline
  darkSlate: [36, 60, 76],     // #243C4C — logo title
  mutedSlate:[105, 134, 150],  // #698696 — TM symbol
  title:     [34, 34, 34],     // #222 — query title, section headings
  body:      [51, 51, 51],     // #333 — body text, header rule
  meta:      [136, 136, 136],  // #888 — metadata, footer
  cite:      [85, 85, 85],     // #555 — citation superscripts
  ruleLight: [204, 204, 204],  // #ccc — section underlines, footer rule
  ruleMed:   [221, 221, 221],  // #ddd — table row borders
  white:     [255, 255, 255],
};

// =========================================
// Text Sanitization (Latin-1 safe for jsPDF)
// =========================================

/**
 * Sanitize text for jsPDF's built-in Helvetica (WinAnsiEncoding).
 * Em dashes (U+2014) and en dashes (U+2013) render correctly in WinAnsi
 * so they are preserved. Curly quotes and other unsupported characters
 * are replaced with ASCII equivalents or stripped.
 */
function sanitizeForPdf(text) {
  if (!text) return '';

  // STEP 1 — NFC FIRST. An accented letter can arrive precomposed (U+00E1 "á")
  // or decomposed ("a" + U+0301 combining acute). Latin-1 holds the first and
  // not the second, so without this the same character behaved differently
  // depending on how it was typed: a precomposed "á" rendered, a decomposed one
  // lost its accent to the fallback below. NFC composes every pair that has a
  // precomposed form, which covers most European accents.
  let s = String(text)
    .normalize('NFC')
    .replace(/[\u201C\u201D]/g, '"')    // curly double quotes → straight
    .replace(/[\u2018\u2019]/g, "'")    // curly single quotes → straight
    .replace(/\u2026/g, '...')          // ellipsis → three dots
    .replace(/\u2122/g, '')             // trademark — rendered as superscript separately
    .replace(/\u00A9/g, '(C)')         // copyright
    .replace(/\u00AE/g, '(R)');        // registered

  // STEP 2 - TRANSLITERATE WHAT LATIN-1 CANNOT HOLD (interim fix, ruled
  // 2026-10-09 D6). A letter with no precomposed Latin-1 form - U+0113 (e with
  // macron), U+0101, U+015B, U+016F - is decomposed and stripped of its
  // combining marks so the base letter survives: "melet" + U+0113 renders as
  // "melete", not "melet[?]".
  //
  // SCOPED TO CHARACTERS OUTSIDE LATIN-1 ON PURPOSE. U+00E9, U+00E1, U+00F1 and
  // U+00FC are inside it, WinAnsi renders them, and they must pass through
  // unchanged - so this replacer never sees them.
  //
  // A character with no Latin base (Greek, Cyrillic, CJK) decomposes to itself,
  // fails the Latin-1 test, and is returned unchanged for the fallback below to
  // turn into [?]. En/em dashes take the same path and are then exempted by the
  // fallback, so they survive. The `u` flag makes this iterate by CODE POINT, so
  // an astral character is one unit rather than two surrogate halves.
  //
  // INTERIM, NOT COMPLETE. Real support for non-Latin scripts needs an embedded
  // Unicode font; that trade-off is tracked in docs/TODO.md.
  // eslint-disable-next-line no-control-regex
  s = s.replace(/[^\x00-\xFF]/gu, (ch) => {
    const base = ch.normalize('NFD').replace(/\p{M}/gu, '');
    // eslint-disable-next-line no-control-regex
    return base.length > 0 && /^[\x00-\xFF]*$/.test(base) ? base : ch;
  });

  return s
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x00-\xFF\u2013\u2014]/gu, '[?]')  // no Latin base; em/en dashes kept
    .replace(/[\t\r\n]+/g, ' ')        // normalize whitespace chars to spaces
    .replace(/ {2,}/g, ' ');           // collapse multiple spaces
}

/**
 * Build the download file name's slug from the user's QUESTION.
 *
 * Changed 2026-10-09 (ruled). The slug used to be built from `conceptTags` —
 * the most frequent tags in the retrieved bundle — which named the file after
 * what happened to be retrieved rather than what was asked. A question about
 * password managers downloaded as
 * CKG_20261009_artificial-intelligence-the-internet-ai-doge-elon-musk.pdf.
 *
 * Lowercased, ASCII, hyphen-separated, punctuation removed, and truncated at a
 * WORD BOUNDARY to at most 60 characters so the name never ends mid-word.
 * Falls back to 'analysis' when there is no usable question, which preserves the
 * previous fallback and so the CKG_<date>_analysis.pdf name.
 *
 * @param {string} question - The user's original question
 * @returns {string} The slug, never empty
 */
function questionSlug(question) {
  const MAX = 60;

  // Transliterate accents to ASCII before stripping, so an accented word keeps
  // its letters ("reflexion") instead of losing them to the [^a-z0-9] filter.
  const ascii = String(question || '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase();

  const words = ascii
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (words.length === 0) return 'analysis';

  let slug = '';
  for (const word of words) {
    const next = slug ? `${slug}-${word}` : word;
    if (next.length > MAX) break;
    slug = next;
  }

  // A single first word longer than MAX would leave slug empty; take a hard cut
  // rather than return nothing.
  if (!slug) slug = words[0].slice(0, MAX);

  return slug || 'analysis';
}

/**
 * Render text containing "Constellation KG" with a superscript TM.
 * Returns the total width of the rendered text.
 */
function renderBrandedText(doc, prefix, suffix, x, y, fontSize, tmSize) {
  const brandName = 'Constellation KG';
  const savedSize = doc.getFontSize();
  const savedStyle = doc.getFont().fontStyle;

  // Render prefix (e.g., "Generated by ")
  if (prefix) {
    doc.setFontSize(fontSize);
    doc.text(prefix, x, y);
    x += doc.getTextWidth(prefix);
  }

  // Render brand name
  doc.setFontSize(fontSize);
  doc.text(brandName, x, y);
  x += doc.getTextWidth(brandName);

  // Render TM as superscript
  const tmFontSize = tmSize || Math.max(6, fontSize * 0.55);
  doc.setFontSize(tmFontSize);
  doc.setTextColor(...C.mutedSlate);
  doc.text('TM', x + 1, y - fontSize * 0.32);
  const tmW = doc.getTextWidth('TM');
  x += 1 + tmW;

  // Render suffix (e.g., " for personal use")
  if (suffix) {
    doc.setFontSize(fontSize);
    doc.setTextColor(...C.meta);
    doc.text(suffix, x + 0.5, y);
    x += 0.5 + doc.getTextWidth(suffix);
  }

  // Restore
  doc.setFontSize(savedSize);
  doc.setFont('Helvetica', savedStyle || 'normal');
  return x;
}

// =========================================
// Markdown → Plain Text Conversion
// =========================================

/**
 * Convert markdown text into an ordered sequence of BLOCKS for rendering.
 *
 * Returns an array of:
 *   { type: 'paragraph', text }
 *   { type: 'list', ordered, items: [{ marker, text }] }
 *
 * LISTS ARE DETECTED BEFORE PARAGRAPH SPLITTING (added 2026-10-09, D7). The
 * previous markdownToParagraphs() split only on blank lines and turned every
 * single newline into a space, so
 *
 *     - item one
 *     - item two
 *
 * became the one paragraph "- item one - item two", wrapped as prose with the
 * hyphens left in it mid-sentence. A markdown list is single-newline-separated,
 * which is exactly what that collapse destroyed.
 *
 * Both bullet (-, *, +) and ordered (1. / 1\)) markers are recognised. An
 * ordered list keeps its own numbers rather than being renumbered, so a list
 * that starts at 3 still reads 3. Everything that is not a list line is joined
 * into paragraphs exactly as before.
 */
function markdownToBlocks(md) {
  if (!md) return [];

  const BULLET_RE = /^\s*[-*+]\s+(.*)$/;
  const ORDERED_RE = /^\s*(\d+)[.)]\s+(.*)$/;

  const blocks = [];
  let paragraphLines = [];   // lines of the paragraph being accumulated
  let list = null;           // the list being accumulated, if any

  const flushParagraph = () => {
    if (paragraphLines.length === 0) return;
    const joined = paragraphLines.join(' ').trim();
    // Strip # heading markers (h1/h2/h3) that may survive from markdown
    const text = sanitizeForPdf(joined.replace(/^#{1,3}\s+/, ''));
    if (text) blocks.push({ type: 'paragraph', text });
    paragraphLines = [];
  };

  const flushList = () => {
    if (!list) return;
    if (list.items.length > 0) blocks.push(list);
    list = null;
  };

  for (const rawLine of String(md).split('\n')) {
    const bullet = rawLine.match(BULLET_RE);
    const ordered = bullet ? null : rawLine.match(ORDERED_RE);

    if (bullet || ordered) {
      flushParagraph();
      const isOrdered = Boolean(ordered);
      if (!list || list.ordered !== isOrdered) {
        flushList();
        list = { type: 'list', ordered: isOrdered, items: [] };
      }
      const body = sanitizeForPdf(isOrdered ? ordered[2] : bullet[1]);
      if (body) {
        list.items.push({
          marker: isOrdered ? `${ordered[1]}.` : '\u2022',
          text: body
        });
      }
      continue;
    }

    if (rawLine.trim() === '') {
      flushParagraph();
      flushList();
      continue;
    }

    // A non-blank, non-list line continues the current paragraph. It also ends
    // any open list: prose immediately after list items is not an item.
    flushList();
    paragraphLines.push(rawLine.trim());
  }

  flushParagraph();
  flushList();

  return blocks;
}

/**
 * Render a paragraph string into styled segments.
 * Handles **bold**, *italic*, _italic_, and [N](#source-N) citations.
 */
function parseStyledSegments(text) {
  const segments = [];
  // Regex: bold+italic, bold, italic, citation link, or plain text
  const re = /(\*\*\*(.+?)\*\*\*|\*\*(.+?)\*\*|\*(.+?)\*|_(.+?)_|\[(\d+)\]\(#source-\d+\)|\[(\d+)\])/g;
  let lastIndex = 0;
  let match;

  while ((match = re.exec(text)) !== null) {
    // Push plain text before match
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), style: 'normal' });
    }

    if (match[2]) {
      // ***bold italic***
      segments.push({ text: match[2], style: 'bolditalic' });
    } else if (match[3]) {
      // **bold**
      segments.push({ text: match[3], style: 'bold' });
    } else if (match[4]) {
      // *italic*
      segments.push({ text: match[4], style: 'italic' });
    } else if (match[5]) {
      // _italic_
      segments.push({ text: match[5], style: 'italic' });
    } else if (match[6]) {
      // [N](#source-N) citation link
      segments.push({ text: `[${match[6]}]`, style: 'normal', isCitation: true });
    } else if (match[7]) {
      // [N] plain citation
      segments.push({ text: `[${match[7]}]`, style: 'normal', isCitation: true });
    }

    lastIndex = match.index + match[0].length;
  }

  // Trailing plain text
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), style: 'normal' });
  }

  return segments;
}

// =========================================
// PDF Rendering Helpers
// =========================================

/**
 * Add a new page, stamp footer on the current page first.
 */
function addPage(doc) {
  doc.addPage();
}

/**
 * Check if content fits on current page; if not, add new page.
 * Returns the (possibly reset) Y position.
 */
function ensureSpace(doc, y, neededHeight) {
  if (y + neededHeight > CONTENT_BOTTOM) {
    addPage(doc);
    return MARGIN_T;
  }
  return y;
}

/**
 * KEEP A HEADING WITH WHAT FOLLOWS IT (ruled 2026-10-10).
 *
 * A heading is never the last thing on a page. Before drawing one, reserve the
 * heading's own vertical advance PLUS the first block that belongs under it —
 * at least two lines of body text for a prose section, or the column-header row
 * plus the first table row for the sources table. If both will not fit, break
 * first so the heading opens the next page instead of closing this one.
 *
 * This existed only for the prose section headings and the conclusion. The
 * sources table reserved 30pt for its title and 16pt for its column-header row,
 * which is enough for the heading alone — so an export could (and did) leave
 * "Sources Cited" and the column headers stranded at the foot of page 3 with the
 * first row starting page 4.
 *
 * @param {Object} doc - jsPDF instance
 * @param {number} y - current Y
 * @param {number} headingHeight - the heading's own advance, including its rule
 * @param {number} followingHeight - height of the first block that must stay with it
 * @returns {number} the (possibly reset) Y position
 */
function ensureSpaceForHeading(doc, y, headingHeight, followingHeight) {
  return ensureSpace(doc, y, headingHeight + followingHeight);
}

/**
 * Height one sources-table row will occupy, measured the same way the row
 * renderer measures it. Extracted 2026-10-10 so the keep-with-next check above
 * the table title can reserve the FIRST row's real height instead of guessing,
 * and so the pre-check and the renderer cannot drift apart.
 */
function measureSourceRowHeight(doc, entry, colW, lineH, padTop, padBottom) {
  doc.setFontSize(9.5);
  doc.setFont('Helvetica', 'normal');
  const sourceLines = doc.splitTextToSize(sanitizeForPdf(entry.docTitle || 'Untitled'), colW.source);
  const focusLines = doc.splitTextToSize(sanitizeForPdf(entry.focus || ''), colW.focus);
  doc.setFont('Helvetica', 'italic');
  const thoughtLines = doc.splitTextToSize(sanitizeForPdf(entry.thought || ''), colW.thought);

  const maxCellH = Math.max(
    sourceLines.length * lineH,
    focusLines.length * lineH,
    thoughtLines.length * lineH,
    lineH
  );
  return padTop + maxCellH + padBottom;
}

/**
 * Render a styled paragraph into the PDF. Returns the new Y position.
 *
 * WHITESPACE IS THE DOCUMENT'S, NOT SYNTHESISED (rewritten 2026-10-09, D5).
 *
 * The previous implementation split every segment on /\s+/, threw the
 * whitespace away, and re-inserted one space between every pair of tokens,
 * measured once per paragraph in Helvetica normal. Two defects followed from
 * that design, and neither was fixable by tuning the constant:
 *
 *   - A space could be swallowed. The x-position of each word was a running sum
 *     of per-word measured widths plus that one constant, so if a word's
 *     rendered advance exceeded the width measured for it — which happens when
 *     a styled face is wider than the face used to measure it — the surplus ate
 *     into the following gap. At 11pt a ~3pt space disappears on a small error,
 *     which is why only emphasised words were affected ("Yourmost", "TechBros").
 *   - A space could be invented. Because a space went between EVERY pair of
 *     tokens, "God[4](#source-4)." rendered as "God [4] ." — the citation and
 *     its punctuation pushed apart by spacing the source never contained.
 *
 * Now: the text is atomised into word and whitespace atoms with the whitespace
 * KEPT, each atom is measured and drawn in its own font, and no space is ever
 * added that the source did not contain. A run of whitespace collapses to one
 * space (sanitizeForPdf already does this for the text it touches) and a space
 * is dropped when it falls at a line break, which is the one place a rendered
 * line should not keep it.
 *
 * Consecutive non-space atoms form one unbreakable WORD even when their styles
 * differ, so "God", "[4]" and "." wrap together and stay joined.
 */
function renderStyledParagraph(doc, segments, x, y, maxWidth, fontSize, lineHeight) {
  // --- Atomise -------------------------------------------------------------
  // split(/(\s+)/) keeps the separators, so whitespace survives as its own atom.
  const atoms = [];
  for (const seg of (segments || [])) {
    const text = typeof seg?.text === 'string' ? seg.text : '';
    if (!text) continue;
    for (const piece of text.split(/(\s+)/)) {
      if (piece === '') continue;
      const isSpace = /^\s+$/.test(piece);
      atoms.push({
        text: isSpace ? ' ' : piece,
        style: seg.style,
        isCitation: seg.isCitation,
        isSpace
      });
    }
  }
  if (atoms.length === 0) return y;

  // --- Font selection, one place so measuring and drawing cannot diverge ----
  const applyFont = (atom) => {
    if (atom.isCitation) {
      doc.setFontSize(9);
      doc.setTextColor(...C.cite);
      doc.setFont('Helvetica', atom.style === 'bold' ? 'bold' : 'normal');
    } else {
      doc.setFontSize(fontSize);
      doc.setTextColor(...C.body);
      doc.setFont('Helvetica',
        atom.style === 'bolditalic' ? 'bolditalic'
          : atom.style === 'bold' ? 'bold'
            : atom.style === 'italic' ? 'italic'
              : 'normal');
    }
  };

  // MEASURE THE WAY A VIEWER ADVANCES, NOT THE WAY jsPDF MEASURES (fixed
  // 2026-10-10). `doc.getTextWidth(str)` applies Helvetica's KERNING table, but
  // `doc.text()` emits a plain `(str) Tj` with no kern array — so a PDF viewer
  // advances by each glyph's unkerned Widths entry and renders the word WIDER
  // than jsPDF said it was. Measured at 11pt: getTextWidth('Your') = 21.45pt
  // against 22.99pt of actual glyph advances, a 1.54pt deficit from the "Yo"
  // kern pair — exactly half a space (3.08pt). The next word was therefore
  // positioned 1.54pt too far left and the space looked closed: "Yourreflection",
  // "Youcaptured", "TechBros" ("Te" kern, 1.32pt), "TranscendentalMeditation".
  // Only words containing a kern pair were affected, which is why most word
  // pairs always looked fine.
  //
  // Summing PER CHARACTER cannot pick up a kern pair, so this returns what the
  // viewer will actually do. Both the wrap arithmetic and the draw advance must
  // use it — if either goes back to measuring whole strings, the bug returns.
  const widthOf = (atom) => {
    applyFont(atom);
    let w = 0;
    for (const ch of atom.text) w += doc.getTextWidth(ch);
    return w;
  };

  // --- Lay out -------------------------------------------------------------
  const right = x + maxWidth;
  let lineX = x;
  let lineY = y;
  let pendingSpace = null;   // a space atom waiting for the word that follows it
  let i = 0;

  while (i < atoms.length) {
    if (atoms[i].isSpace) {
      pendingSpace = atoms[i];
      i += 1;
      continue;
    }

    // One word = consecutive non-space atoms, styles may differ across it.
    const word = [];
    while (i < atoms.length && !atoms[i].isSpace) {
      word.push(atoms[i]);
      i += 1;
    }

    const wordWidth = word.reduce((w, a) => w + widthOf(a), 0);
    const spaceWidth = pendingSpace ? widthOf(pendingSpace) : 0;

    if (lineX > x && lineX + spaceWidth + wordWidth > right) {
      // Wrap. The pending space is dropped: a wrapped line never opens with one.
      lineX = x;
      lineY += lineHeight;
      lineY = ensureSpace(doc, lineY, lineHeight);
    } else if (pendingSpace) {
      lineX += spaceWidth;
    }
    pendingSpace = null;

    for (const atom of word) {
      applyFont(atom);
      doc.text(atom.text, lineX, lineY);
      // widthOf, NOT doc.getTextWidth — see the note on widthOf above. This is
      // the line that actually positioned the next atom too far left.
      lineX += widthOf(atom);
    }
  }

  // Leave the document in the body style, as the previous implementation did.
  doc.setFontSize(fontSize);
  doc.setTextColor(...C.body);
  doc.setFont('Helvetica', 'normal');

  return lineY + lineHeight;
}

/**
 * Render a markdown block sequence (paragraphs and lists) from one markdown
 * string. Returns the new Y position.
 *
 * Added 2026-10-09 (D7). Replaces four identical paragraph loops that each
 * called markdownToParagraphs() + parseStyledSegments() + renderStyledParagraph().
 */
function renderMarkdownBlocks(doc, md, y, fontSize, lineHeight) {
  const blocks = markdownToBlocks(md);

  for (const block of blocks) {
    if (block.type === 'list') {
      for (const item of block.items) {
        y = ensureSpace(doc, y, lineHeight * 2);

        // Marker in the left gutter, body indented and wrapped inside it.
        doc.setFontSize(fontSize);
        doc.setFont('Helvetica', 'normal');
        doc.setTextColor(...C.body);
        doc.text(item.marker, MARGIN_L, y);

        const bodyX = MARGIN_L + LIST_INDENT;
        y = renderStyledParagraph(
          doc,
          parseStyledSegments(item.text),
          bodyX,
          y,
          CONTENT_W - LIST_INDENT,
          fontSize,
          lineHeight
        );
        y += 2; // tight spacing between items
      }
      y += 6; // space after the list
      continue;
    }

    y = ensureSpace(doc, y, lineHeight * 2);
    y = renderStyledParagraph(
      doc,
      parseStyledSegments(block.text),
      MARGIN_L,
      y,
      CONTENT_W,
      fontSize,
      lineHeight
    );
    y += 6; // paragraph spacing
  }

  return y;
}

// =========================================
// Sources Table
// =========================================

function renderSourcesTable(doc, citedEntries, startY) {
  let y = startY;

  // Column layout — proportional widths with inter-column padding.
  // Computed BEFORE the title (moved 2026-10-10) because the title's
  // keep-with-next check needs the first row's measured height, and measuring a
  // row needs the column widths.
  const COL_PAD = 5;
  const colAlloc = {
    num:     Math.floor(CONTENT_W * 0.06),   // 6%  = 30
    source:  Math.floor(CONTENT_W * 0.22),   // 22% = 110
    focus:   Math.floor(CONTENT_W * 0.36),   // 36% = 180
    thought: Math.floor(CONTENT_W * 0.36),   // 36% = 180
  };
  const colX = {
    num:     MARGIN_L,
    source:  MARGIN_L + colAlloc.num,
    focus:   MARGIN_L + colAlloc.num + colAlloc.source,
    thought: MARGIN_L + colAlloc.num + colAlloc.source + colAlloc.focus,
  };
  const colW = {
    num:     colAlloc.num - COL_PAD,
    source:  colAlloc.source - COL_PAD,
    focus:   colAlloc.focus - COL_PAD,
    thought: colAlloc.thought - COL_PAD,
  };

  // Row metrics, needed by the keep-with-next checks below as well as the loop.
  const lineH = 12;
  const padTop = 8;
  const padBottom = 8;

  const sorted = [...citedEntries].sort((a, b) => a.citationNumber - b.citationNumber);

  // Heights of the three things that must not be separated: the title's advance,
  // the column-header row (text + rule + gap), and the first data row.
  const TITLE_H = 20;
  const HEADER_H = 14;
  const firstRowH = sorted.length
    ? measureSourceRowHeight(doc, sorted[0], colW, lineH, padTop, padBottom)
    : lineH;

  // Title — kept with the column headers AND the first row.
  y = ensureSpaceForHeading(doc, y, TITLE_H, HEADER_H + firstRowH);
  doc.setFontSize(16);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(...C.title);
  doc.text('Sources Cited', MARGIN_L, y);
  y += TITLE_H;

  // Header — kept with the first row on its own account, for the case where the
  // title fitted comfortably but the rows no longer do.
  y = ensureSpaceForHeading(doc, y, HEADER_H, firstRowH);
  doc.setFontSize(9.5);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(...C.title);
  doc.text('#', colX.num, y);
  doc.text('Source', colX.source, y);
  doc.text('Focus', colX.focus, y);
  doc.text('Your Thought', colX.thought, y);
  y += 4;
  doc.setDrawColor(...C.body);
  doc.setLineWidth(1);
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
  y += 10;

  // Rows
  for (const entry of sorted) {
    const sourceText = sanitizeForPdf(entry.docTitle || 'Untitled');
    const focusText = sanitizeForPdf(entry.focus || '');
    const thoughtText = sanitizeForPdf(entry.thought || '');

    // Wrap each cell's text to get line arrays
    doc.setFontSize(9.5);
    doc.setFont('Helvetica', 'normal');
    const sourceLines = doc.splitTextToSize(sourceText, colW.source);
    const focusLines = doc.splitTextToSize(focusText, colW.focus);
    doc.setFont('Helvetica', 'italic');
    const thoughtLines = doc.splitTextToSize(thoughtText, colW.thought);

    // Row height, measured by the shared helper so this and the keep-with-next
    // checks above the title can never disagree.
    const rowHeight = measureSourceRowHeight(doc, entry, colW, lineH, padTop, padBottom);

    // Page break check — if the full row doesn't fit, start a new page
    y = ensureSpace(doc, y, rowHeight);

    const rowStartY = y;
    const textY = rowStartY + padTop; // all cells start here

    // Citation number
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(...C.title);
    doc.text(`[${entry.citationNumber}]`, colX.num, textY);

    // Source
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(...C.body);
    let cellY = textY;
    for (const line of sourceLines) {
      doc.text(line, colX.source, cellY);
      cellY += lineH;
    }

    // Focus
    cellY = textY;
    for (const line of focusLines) {
      doc.text(line, colX.focus, cellY);
      cellY += lineH;
    }

    // Thought (italic)
    doc.setFont('Helvetica', 'italic');
    cellY = textY;
    for (const line of thoughtLines) {
      doc.text(line, colX.thought, cellY);
      cellY += lineH;
    }

    // Divider at bottom of row: rowStart + padTop + tallest cell + padBottom
    const dividerY = rowStartY + rowHeight;
    doc.setDrawColor(...C.ruleMed);
    doc.setLineWidth(0.5);
    doc.line(MARGIN_L, dividerY, PAGE_W - MARGIN_R, dividerY);

    // Next row starts just below the divider
    y = dividerY + 4;
  }

  return y;
}

// =========================================
// Footer Stamping
// =========================================

function stampFooters(doc) {
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);

    // Horizontal rule — inside the reserved footer zone
    doc.setDrawColor(...C.ruleLight);
    doc.setLineWidth(0.5);
    doc.line(MARGIN_L, FOOTER_RULE_Y, PAGE_W - MARGIN_R, FOOTER_RULE_Y);

    // Left text — branded with superscript TM (14pt below rule)
    doc.setFontSize(9);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(...C.meta);
    renderBrandedText(doc, 'Generated by ', ' for personal use', MARGIN_L, FOOTER_TEXT_Y, 9, 6);

    // Right text — page number
    doc.setFontSize(9);
    doc.setFont('Helvetica', 'normal');
    doc.setTextColor(...C.meta);
    const pageText = `Page ${i} of ${totalPages}`;
    const pageTextWidth = doc.getTextWidth(pageText);
    doc.text(pageText, PAGE_W - MARGIN_R - pageTextWidth, FOOTER_TEXT_Y);
  }
}

// =========================================
// Main Export Function
// =========================================

/**
 * Generate and download a PDF of an AI Explorer analysis.
 *
 * @param {Object} params
 * @param {string} params.query - User's original question. Rendered as the PDF
 *   title AND, since 2026-10-09, the source of the download file name's slug.
 * @param {Object} params.response - Full API response object
 * @param {Object} params.sections - Output of parseMarkdownSections(response.text)
 * @param {Array} params.citedEntries - response.citedEntries
 * @param {Date} params.timestamp - When the analysis was completed
 */
// `conceptTags` is still passed by the caller and is deliberately NOT
// destructured here: it used to build the file-name slug, and since 2026-10-09
// the slug comes from `query` (see questionSlug). Extra properties are ignored,
// so the caller needed no change. It was never rendered in the PDF body.
export function generateAnalysisPDF({
  query,
  response,
  sections,
  citedEntries,
  timestamp,
}) {
  const doc = new jsPDF({
    unit: 'pt',
    format: 'letter',
  });

  const ts = timestamp || new Date();
  const dateStr = ts.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr = ts.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  const providerName = response.provider === 'claude' ? 'Claude' : 'GPT-4';
  const modelName = response.model || providerName;
  const meta = response.searchMetadata || {};

  // ------------------------------------------
  // Page 1 Header: Logo + Query
  // ------------------------------------------

  let y = MARGIN_T;
  const logoColW = 110;
  const rightColX = MARGIN_L + logoColW + 16;
  const rightColW = CONTENT_W - logoColW - 16;

  // Place logo — hardcoded base64 PNG, no async loading needed
  doc.addImage(LOGO_BASE64, 'PNG', MARGIN_L, y, 100, 100);

  // Logo text below image — sized to fit within the 100pt logo width
  const logoMarkW = 100;
  const logoTextCenterX = MARGIN_L + logoMarkW / 2;

  // Brand name + superscript TM — scale down until it fits within logo width
  const brandName = 'Constellation KG';
  let brandFontSize = 9.5;
  const tmGap = 1;
  doc.setFont('Helvetica', 'bold');
  doc.setFontSize(brandFontSize);
  let brandW = doc.getTextWidth(brandName);
  const tmFontSize = Math.round(brandFontSize * 0.55 * 10) / 10; // ~5pt
  doc.setFontSize(tmFontSize);
  const tmW = doc.getTextWidth('TM');
  const totalBrandW = brandW + tmGap + tmW;

  const brandStartX = logoTextCenterX - totalBrandW / 2;
  // Render brand name
  doc.setFontSize(brandFontSize);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(...C.darkSlate);
  doc.text(brandName, brandStartX, y + 112);
  // Render TM as superscript
  doc.setFontSize(tmFontSize);
  doc.setTextColor(...C.mutedSlate);
  doc.text('TM', brandStartX + brandW + tmGap, y + 112 - 3);

  // Tagline — 8pt, must also fit within logo width
  doc.setFontSize(8);
  doc.setFont('Helvetica', 'normal');
  doc.setTextColor(...C.teal);
  const tagline = 'Explore your knowledge';
  const taglineW = doc.getTextWidth(tagline);
  doc.text(tagline, logoTextCenterX - taglineW / 2, y + 122);

  // Right column: query title
  // Offset by cap-height (~72% of font size) so top of text aligns with top of logo
  doc.setFontSize(20);
  doc.setFont('Helvetica', 'bold');
  doc.setTextColor(...C.title);
  const queryLines = doc.splitTextToSize(sanitizeForPdf(query), rightColW);
  let rightY = y + 15;
  for (const line of queryLines) {
    doc.text(line, rightColX, rightY);
    rightY += 24;
  }

  // Metadata line
  rightY += 4;
  doc.setFontSize(10);
  doc.setFont('Helvetica', 'normal');
  doc.setTextColor(...C.meta);
  doc.text(sanitizeForPdf(`Asked of ${providerName} at ${timeStr} on ${dateStr}`), rightColX, rightY);
  rightY += 16;

  // Disclosure summary — built by the same function the on-screen "Analysis
  // Complete" box uses, so the PDF and the page cannot report different numbers
  // for the same run. They previously did: the box said "determined only 56 of
  // them were relevant" while this header said "determined all of them were
  // relevant", from identical metadata. Do not inline the wording here.
  //
  // Gated on the sentence being non-empty rather than on meta.totalAnalyzed —
  // totalAnalyzed is the retrieval cap and no longer appears in the sentence, so
  // keying the block on it would suppress the line whenever that field is absent
  // even though the citation counts are present.
  const disclosureText = sanitizeForPdf(buildAnalysisSummary(meta));
  if (disclosureText) {
    const discLines = doc.splitTextToSize(disclosureText, rightColW);
    for (const line of discLines) {
      doc.text(line, rightColX, rightY);
      rightY += 13;
    }
  }

  // Move Y below whichever column is taller
  y = Math.max(y + 136, rightY + 12);

  // Header rule
  doc.setDrawColor(...C.body);
  doc.setLineWidth(1);
  doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
  y += 20;

  // ------------------------------------------
  // Body Content
  // ------------------------------------------

  const bodyLineHeight = 18; // ~1.65 line height at 11pt

  // Intro
  if (sections.intro) {
    y = renderMarkdownBlocks(doc, sections.intro, y, 11, bodyLineHeight);
    y += 6;
  }

  // Body sections
  if (sections.bodySections) {
    for (const section of sections.bodySections) {
      // Section heading — kept with its first lines of body text (2026-10-10:
      // now via the shared helper, so every heading in the file obeys one rule).
      const headingHeight = 24;
      const minContentHeight = bodyLineHeight * 3;
      y = ensureSpaceForHeading(doc, y, headingHeight, minContentHeight);

      // Heading
      doc.setFontSize(15);
      doc.setFont('Helvetica', 'bold');
      doc.setTextColor(...C.title);
      doc.text(sanitizeForPdf(section.headingText), MARGIN_L, y);
      y += 9;
      doc.setDrawColor(...C.ruleLight);
      doc.setLineWidth(0.5);
      doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
      y += 22;

      // Section content
      y = renderMarkdownBlocks(doc, section.content, y, 11, bodyLineHeight);
      y += 10; // section spacing
    }
  }

  // Conclusion
  if (sections.conclusion) {
    // Conclusion heading — kept with its first lines of body text.
    const headingHeight = 24;
    const minContentHeight = bodyLineHeight * 3;
    y = ensureSpaceForHeading(doc, y, headingHeight, minContentHeight);

    doc.setFontSize(15);
    doc.setFont('Helvetica', 'bold');
    doc.setTextColor(...C.title);
    doc.text('Conclusion', MARGIN_L, y);
    y += 9;
    doc.setDrawColor(...C.ruleLight);
    doc.setLineWidth(0.5);
    doc.line(MARGIN_L, y, PAGE_W - MARGIN_R, y);
    y += 22;

    y = renderMarkdownBlocks(doc, sections.conclusion, y, 11, bodyLineHeight);
    y += 10;
  }

  // Handle rawText fallback (no sections parsed)
  if (sections.rawText) {
    y = renderMarkdownBlocks(doc, sections.rawText, y, 11, bodyLineHeight);
  }

  // ------------------------------------------
  // Sources Cited Table
  // ------------------------------------------

  if (citedEntries && citedEntries.length > 0) {
    // Start on new page if less than 100pt remaining
    y = ensureSpace(doc, y, 100);
    y = renderSourcesTable(doc, citedEntries, y);
  }

  // ------------------------------------------
  // Stamp Footers on All Pages
  // ------------------------------------------

  stampFooters(doc);

  // ------------------------------------------
  // Download
  // ------------------------------------------

  const dateSlug = ts.toISOString().slice(0, 10).replace(/-/g, '');
  const filename = `CKG_${dateSlug}_${questionSlug(query)}.pdf`;
  doc.save(filename);
}
